import fromPairs from "lodash/fromPairs";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import reduce from "lodash/reduce";
import times from "lodash/times";
import moment from "moment";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { NormalizedRange } from "PFComponents/table";
import { ISO_DATE_FORMAT } from "PFCore/helpers/date";

import { AvailabilityTableRow, CalendarDuration, Week } from "./types";

const MONDAY = 1;
const SUNDAY = 7;
const SIX_MONTHS = 6;

export const MAX_HOURS_PER_WEEK = 7 * 24;
export const HOURS_TO_MINUTES = 60;

export const WEEK_COLUMN_WIDTH = 120;

export function clampHours(raw: unknown): number | null {
  if (isNull(raw) || isUndefined(raw)) {
    return null;
  }

  const hours = typeof raw === "number" ? raw : parseFloat(String(raw));

  if (isNaN(hours)) {
    return null;
  }

  return Math.min(Math.max(0, hours), MAX_HOURS_PER_WEEK);
}

export const getCalendarDuration = (startDate?: string, endDate?: string): CalendarDuration => {
  const start = startDate ? moment.utc(startDate) : moment.utc();
  const weekStart = start.isoWeekday() === MONDAY ? start : start.clone().startOf("week");

  let weekEnd;

  if (endDate) {
    const end = moment.utc(endDate);
    weekEnd = end.isoWeekday() === SUNDAY ? end : end.clone().endOf("week");
  } else {
    const sixMonthsLater = weekStart.clone().add(SIX_MONTHS, "months");
    weekEnd = sixMonthsLater.isoWeekday() === SUNDAY ? sixMonthsLater : sixMonthsLater.clone().endOf("week");
  }

  return {
    start: weekStart.format(ISO_DATE_FORMAT),
    end: weekEnd.format(ISO_DATE_FORMAT)
  };
};

export const buildRangeUpdates = (
  { minRow, maxRow, minCol, maxCol }: NormalizedRange,
  weekCount: number,
  roleFormKeys: RoleFormKey[],
  disabledRoleFormKeys: RoleFormKey[],
  getValue: (rowIndex: number, colIndex: number) => number | null
): Record<RoleFormKey, AvailabilityTableRow> =>
  reduce(
    times(maxRow - minRow + 1, (offset) => minRow + offset),
    (acc, rowIndex) => {
      const roleFormKey = roleFormKeys[rowIndex];

      if (!roleFormKey || disabledRoleFormKeys.includes(roleFormKey)) {
        return acc;
      }

      const rowUpdates = fromPairs(
        times(maxCol - minCol + 1, (offset) => minCol + offset)
          .filter((colIndex) => colIndex < weekCount)
          .map((colIndex) => [colIndex, getValue(rowIndex, colIndex)])
      );

      return { ...acc, [roleFormKey]: rowUpdates };
    },
    {} as Record<RoleFormKey, AvailabilityTableRow>
  );

export const generateWeeks = (calendarDuration: CalendarDuration): Week[] => {
  const start = moment.utc(calendarDuration.start);
  const end = moment.utc(calendarDuration.end);
  const weeksDiff = end.diff(start, "weeks") + 1;

  return Array.from({ length: weeksDiff }, (_, index) => {
    const startDate = start.clone().add(index, "weeks");
    const endDate = startDate.clone().endOf("week");

    return {
      startDate: startDate.format(ISO_DATE_FORMAT),
      endDate: endDate.format(ISO_DATE_FORMAT)
    };
  });
};

export const hasRoleListShifted = (
  previousKeys: (string | number)[],
  nextKeys: (string | number)[]
): boolean =>
  previousKeys.length !== nextKeys.length ||
  previousKeys.some((key, index) => nextKeys[index] !== key && nextKeys.includes(key));
